import { createFileRoute, redirect } from '@tanstack/react-router'
import clsx from 'clsx'
import type { ReactElement } from 'react'
import z from 'zod'
import logoUrl from '~/assets/job-explorer.svg'
import { InputCol } from '~/components/Form'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { Paper } from '~/components/Global/Blocks/Paper/Paper'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import {
  OAuthConsentError,
  useOAuthConsent
} from '~/domains/oauth/mutations/useOAuthConsent'
import {
  getScopeLabel,
  useOAuthClientInfo
} from '~/domains/oauth/queries/useOAuthClientInfo'
import {
  organizationLogo,
  organizationName
} from '~/domains/orgs/components/OrganizationSelector.css'
import { getStoredOrganizations } from '~/domains/orgs/utils/organizationStorage'
import { themeClass } from '~/styles/theme.css'
import useDocumentTitle from '~/utils/documentTitle'
import {
  card,
  clientName,
  container,
  logo,
  organization,
  scopeDot,
  scopeItem,
  scopesContainer,
  scopesList,
  title
} from './-consent.css'

const searchSchema = z.object({
  client_id: z.string(),
  scope: z.string().optional(),
  response_type: z.string().optional(),
  redirect_uri: z.string().optional(),
  state: z.string().optional(),
  code_challenge: z.string().optional(),
  code_challenge_method: z.string().optional()
})

type ConsentSearch = z.infer<typeof searchSchema>

const loginUrl = (): string =>
  `/?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`

const isFramed = (): boolean => {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

const toOAuthParams = (search: ConsentSearch): string => {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(search)) {
    if (typeof value === 'string') {
      params.set(key, value)
    }
  }
  return params.toString()
}

const consentErrorMessage = (error: unknown): string => {
  if (error instanceof OAuthConsentError && error.status === 401) {
    return 'Votre session job explorer a expiré. Reconnectez-vous pour continuer.'
  }
  return error instanceof Error ? error.message : 'Une erreur est survenue'
}

const OAuthConsentPage = (): ReactElement => {
  useDocumentTitle('Autoriser une application')
  const search = Route.useSearch()
  const { client_id } = search

  const { data: clientInfo, isLoading, isError } = useOAuthClientInfo(client_id)
  const consent = useOAuthConsent()

  const oauthParams = toOAuthParams(search)

  const currentOrganization = localStorage.getItem('instance') ?? ''
  const storedOrganization = getStoredOrganizations().find(
    (o) => o.slug === currentOrganization
  )
  const organizationLabel = storedOrganization?.name ?? currentOrganization

  const handleApprove = (): void => {
    consent.mutate({ approved: true, oauthParams })
  }

  const handleDeny = (): void => {
    consent.mutate({ approved: false, oauthParams })
  }

  const requestedScopes = search.scope?.split(' ').filter(Boolean) ?? []
  const sessionExpired =
    consent.error instanceof OAuthConsentError && consent.error.status === 401

  // Clickjacking guard: never offer the Authorize button inside a frame
  if (isFramed()) {
    return (
      <div className={clsx(themeClass, container)}>
        <Paper className={card}>
          <img src={logoUrl} alt="job explorer" className={logo} />
          <Alert variation={AlertVariation.Danger} onDismiss={false}>
            Cette page ne peut pas être affichée dans un cadre. Ouvrez-la
            directement dans votre navigateur.
          </Alert>
        </Paper>
      </div>
    )
  }

  return (
    <div className={clsx(themeClass, container)}>
      <Paper className={card}>
        <img src={logoUrl} alt="job explorer" className={logo} />

        {isLoading && <Loader small forceText />}

        {isError && (
          <Alert variation={AlertVariation.Danger} onDismiss={false}>
            Application introuvable ou inactive.
          </Alert>
        )}

        {clientInfo && (
          <InputCol>
            <h1 className={title}>
              <span className={clientName}>{clientInfo.name}</span> souhaite
              accéder à votre compte
            </h1>

            <div className={organization}>
              {storedOrganization?.logoHeader ? (
                <img
                  src={storedOrganization.logoHeader}
                  alt={organizationLabel}
                  className={organizationLogo}
                />
              ) : (
                <div className={organizationLogo}>
                  {organizationLabel.charAt(0)}
                </div>
              )}
              <div className={organizationName}>{organizationLabel}</div>
            </div>

            {requestedScopes.length > 0 && (
              <div className={scopesContainer}>
                Cette application recevra les autorisations suivantes :
                <ul className={scopesList}>
                  {requestedScopes.map((scope) => (
                    <li key={scope} className={scopeItem}>
                      <span className={scopeDot} />
                      {getScopeLabel(scope)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {consent.error && (
              <Alert variation={AlertVariation.Danger} onDismiss={false}>
                {consentErrorMessage(consent.error)}
              </Alert>
            )}

            <ButtonGroup align="center">
              {sessionExpired ? (
                <Button
                  type="button"
                  variation={ButtonVariation.Primary}
                  onClick={() => {
                    window.location.href = loginUrl()
                  }}
                >
                  Se reconnecter
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variation={ButtonVariation.Danger}
                    onClick={handleDeny}
                    disabled={consent.isPending}
                  >
                    Refuser
                  </Button>
                  <Button
                    type="button"
                    variation={ButtonVariation.Primary}
                    onClick={handleApprove}
                    disabled={consent.isPending}
                  >
                    Autoriser
                  </Button>
                </>
              )}
            </ButtonGroup>
          </InputCol>
        )}
      </Paper>
    </div>
  )
}

export const Route = createFileRoute('/oauth/consent')({
  validateSearch: searchSchema,
  // Same guard as the dashboard: an anonymous visitor is sent to the login
  // page and comes back here once authenticated (redirect search param)
  beforeLoad: ({ context, location }) => {
    if (!context.currentUser) {
      const to: string = `/?redirect=${encodeURIComponent(location.href)}`
      throw redirect({ to })
    }
  },
  component: OAuthConsentPage
})
